/**
 * SubmissionDetail Component
 * Displays full submission information with game data preview
 */

import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSubmission } from '../../hooks/useSubmissions';
import { Loading } from '../Common/Loading';
import { ErrorMessage } from '../Common/ErrorMessage';
import { SubmissionStatusBadge } from './SubmissionStatusBadge';
import {
  SCORING_TYPE_LABELS,
  ROUND_BEHAVIOR_LABELS
} from '../../utils/constants';
import { format } from 'date-fns';

export const SubmissionDetail = ({ submissionId }) => {
  const { data: submission, isLoading, error, refetch } = useSubmission(submissionId);

  // Parse gameData
  const gameData = useMemo(() => {
    if (!submission?.gameData) return {};
    return typeof submission.gameData === 'string'
      ? JSON.parse(submission.gameData)
      : submission.gameData;
  }, [submission]);

  if (!submissionId) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Select a submission to view details
        </Typography>
      </Box>
    );
  }

  if (isLoading) return <Loading message="Loading submission..." />;
  if (error) return <ErrorMessage error={error} onRetry={refetch} />;
  if (!submission) return <Alert severity="error">Submission not found</Alert>;

  // Format scoring type for display
  const getScoringTypeLabel = (type) => {
    if (SCORING_TYPE_LABELS[type]) {
      return SCORING_TYPE_LABELS[type];
    }
    return type || '—';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5">{submission.gameName}</Typography>
          <SubmissionStatusBadge status={submission.status} />
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={() => refetch()} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Info Request & Response - shown when there's a followup message or response */}
      {(submission.followupMessage || submission.contributorResponse) && (
        <Card sx={{ mb: 2, bgcolor: submission.contributorResponse ? 'success.50' : 'warning.50' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {submission.contributorResponse ? 'Contributor Response' : 'Awaiting Response'}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {submission.followupMessage && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Question Asked:</Typography>
                <Paper sx={{ p: 2, bgcolor: 'background.paper', mt: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {submission.followupMessage}
                  </Typography>
                </Paper>
              </Box>
            )}

            {submission.contributorResponse ? (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Response Received {submission.responseReceivedAt &&
                    `(${format(new Date(submission.responseReceivedAt), 'PPpp')})`}:
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'background.paper', mt: 1, border: '1px solid', borderColor: 'success.main' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {submission.contributorResponse}
                  </Typography>
                </Paper>
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Waiting for contributor to respond...
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Submission Info */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Submission Info</Typography>
              <Divider sx={{ mb: 2 }} />
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Contributor</TableCell>
                    <TableCell>{submission.contributor?.handle || '—'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell>{submission.contributor?.email || '—'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Submitted</TableCell>
                    <TableCell>
                      {submission.submittedAt
                        ? format(new Date(submission.submittedAt), 'PPpp')
                        : '—'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Last Updated</TableCell>
                    <TableCell>
                      {submission.updatedAt
                        ? format(new Date(submission.updatedAt), 'PPpp')
                        : '—'}
                    </TableCell>
                  </TableRow>
                  {submission.adminNotes && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Admin Notes</TableCell>
                      <TableCell>{submission.adminNotes}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Game Configuration</Typography>
              <Divider sx={{ mb: 2 }} />
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Game Code</TableCell>
                    <TableCell>{submission.gameCode || '—'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Language</TableCell>
                    <TableCell>{submission.language || 'en'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                    <TableCell>{submission.category || '—'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Players</TableCell>
                    <TableCell>
                      {gameData.minPlayers && gameData.maxPlayers
                        ? `${gameData.minPlayers} - ${gameData.maxPlayers}`
                        : '—'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Scoring Type</TableCell>
                    <TableCell>{getScoringTypeLabel(gameData.scoringType)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Win Condition</TableCell>
                    <TableCell>{gameData.winCondition || '—'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Game Type Info */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Game Type</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              label={submission.isExpansion ? 'Expansion' : 'Base Game'}
              color={submission.isExpansion ? 'secondary' : 'default'}
              size="small"
            />
            <Chip
              label={submission.isVariant ? 'Variant' : 'Standard'}
              color={submission.isVariant ? 'secondary' : 'default'}
              size="small"
            />
          </Box>
          {(submission.parentGameCode || submission.originalGameCode) && (
            <Table size="small">
              <TableBody>
                {submission.parentGameCode && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Parent Game</TableCell>
                    <TableCell>{submission.parentGameCode}</TableCell>
                  </TableRow>
                )}
                {submission.originalGameCode && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Original Game</TableCell>
                    <TableCell>{submission.originalGameCode}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Round Configuration */}
      {gameData.roundConfig && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Round Configuration</Typography>
            <Divider sx={{ mb: 2 }} />
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Sequential</TableCell>
                  <TableCell>{gameData.roundConfig.sequential !== false ? 'Yes' : 'No'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Expanding</TableCell>
                  <TableCell>{gameData.roundConfig.expanding ? 'Yes' : 'No'}</TableCell>
                </TableRow>
                {gameData.roundConfig.behavior && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Behavior</TableCell>
                    <TableCell>
                      {ROUND_BEHAVIOR_LABELS[gameData.roundConfig.behavior] || gameData.roundConfig.behavior}
                    </TableCell>
                  </TableRow>
                )}
                {gameData.roundConfig.titles?.length > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Round Titles</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {gameData.roundConfig.titles.map((title, idx) => (
                          <Chip key={idx} label={title} size="small" />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Scoring Categories */}
      {gameData.scoringCategories?.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Scoring Categories ({gameData.scoringCategories.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Formula</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gameData.scoringCategories.map((cat, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{cat.id}</TableCell>
                    <TableCell>{cat.title || '—'}</TableCell>
                    <TableCell>{cat.formula || '—'}</TableCell>
                    <TableCell>{cat.action || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Setup Text */}
      {gameData.setupText && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Setup Instructions</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {gameData.setupText}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Raw JSON Preview */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Raw Game Data</Typography>
          <Divider sx={{ mb: 2 }} />
          <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 300, overflow: 'auto' }}>
            <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.75rem' }}>
              {JSON.stringify(gameData, null, 2)}
            </pre>
          </Paper>
        </CardContent>
      </Card>
    </Box>
  );
};
